import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Save, Eye, Plus, Trash2, ExternalLink, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  DigitalProposalTemplate,
  PRELOADED_BROCHURES,
  PRELOADED_LENDERS,
  BrochureItem,
  CertificationItem,
  FinancingLender,
} from "@/types/digitalProposalTemplate";
import type { DigitalProposalTier, TierKey } from "@/types/digitalProposal";

const DEFAULT_TIERS: DigitalProposalTier[] = [
  { key: "good", name: "Essential", source: "preset", price: 0, included: ["Standard 3-tab shingles", "Basic underlayment", "Manufacturer warranty"], colors: [] },
  { key: "better", name: "Recommended", source: "preset", price: 0, included: ["Architectural shingles", "Synthetic underlayment", "Ridge vent", "10-yr workmanship warranty"], colors: [], highlight: true },
  { key: "best", name: "Premium", source: "preset", price: 0, included: ["Designer shingles", "Ice & water shield", "Lifetime warranty", "25-yr workmanship"], colors: [] },
];

const EMPTY_TEMPLATE = (orgId: string, orgName?: string): Partial<DigitalProposalTemplate> => ({
  org_id: orgId,
  company_name: orgName || "",
  primary_color: "#0F172A",
  hero_eyebrow: "Project Proposal",
  hero_subheadline: "A premium, no-surprises plan for your home.",
  about_headline: "Why homeowners choose us",
  about_body:
    "We've built our reputation one roof at a time — using premium materials, certified installers, and craftsmanship backed by an industry-leading workmanship warranty.",
  certifications: [],
  brochures: PRELOADED_BROCHURES.map((b, i) => ({ ...b, id: `seed-${i}` })),
  default_tiers: DEFAULT_TIERS,
  financing_enabled: true,
  financing_lenders: PRELOADED_LENDERS,
  default_terms:
    "This proposal is valid for 30 days. Final pricing may adjust for unforeseen conditions. Workmanship warranty per manufacturer specifications. Payment due upon completion unless otherwise agreed.",
  deposit_required: false,
  deposit_percent: 0,
});

export function DigitalProposalTemplateEditor() {
  const { orgId, organization } = useAuth() as any;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [template, setTemplate] = useState<Partial<DigitalProposalTemplate> | null>(null);
  const [previewOpen, setPreviewOpen] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId) return;
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from("digital_proposal_templates")
        .select("*")
        .eq("org_id", orgId)
        .maybeSingle();
      if (data) {
        setTemplate(data as any);
      } else {
        setTemplate(EMPTY_TEMPLATE(orgId, organization?.name));
      }
      setLoading(false);
    })();
  }, [orgId, organization?.name]);

  const update = <K extends keyof DigitalProposalTemplate>(key: K, value: any) =>
    setTemplate((t) => ({ ...(t || {}), [key]: value }));

  const save = async () => {
    if (!template || !orgId) return;
    setSaving(true);
    try {
      const payload = { ...template, org_id: orgId };
      delete (payload as any).created_at;
      delete (payload as any).updated_at;

      const { data, error } = await supabase
        .from("digital_proposal_templates")
        .upsert(payload as any, { onConflict: "org_id" })
        .select("*")
        .single();
      if (error) throw error;
      setTemplate(data as any);
      toast.success("Digital proposal template saved");
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !template) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Digital Proposal Template
          </h2>
          <p className="text-sm text-muted-foreground">
            This is the customer-facing webpage every digital proposal is built from. Set it up once — your reps just adjust prices &amp; colors per job.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.open("/digital-proposal/preview", "_blank")} className="gap-2">
            <Eye className="w-4 h-4" /> Preview
          </Button>
          <Button onClick={save} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Template
          </Button>
        </div>
      </div>

      <Tabs defaultValue="branding" className="w-full">
        <TabsList className="grid grid-cols-5 w-full max-w-2xl">
          <TabsTrigger value="branding">Branding</TabsTrigger>
          <TabsTrigger value="about">About</TabsTrigger>
          <TabsTrigger value="brochures">Brochures</TabsTrigger>
          <TabsTrigger value="tiers">Good/Better/Best</TabsTrigger>
          <TabsTrigger value="financing">Financing &amp; Terms</TabsTrigger>
        </TabsList>

        {/* BRANDING */}
        <TabsContent value="branding" className="mt-6">
          <Card className="p-6 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Company name</Label>
                <Input value={template.company_name || ""} onChange={(e) => update("company_name", e.target.value)} />
              </div>
              <div>
                <Label>Primary brand color</Label>
                <div className="flex gap-2">
                  <Input type="color" value={template.primary_color || "#0F172A"} onChange={(e) => update("primary_color", e.target.value)} className="w-20 h-10 p-1" />
                  <Input value={template.primary_color || ""} onChange={(e) => update("primary_color", e.target.value)} />
                </div>
              </div>
              <div>
                <Label>Logo URL</Label>
                <Input value={template.logo_url || ""} placeholder="https://…/logo.png" onChange={(e) => update("logo_url", e.target.value)} />
              </div>
              <div>
                <Label>Default hero/cover photo URL</Label>
                <Input value={template.hero_photo_url || ""} placeholder="Used when no job photo is available" onChange={(e) => update("hero_photo_url", e.target.value)} />
              </div>
              <div>
                <Label>Hero eyebrow</Label>
                <Input value={template.hero_eyebrow || ""} onChange={(e) => update("hero_eyebrow", e.target.value)} />
              </div>
              <div>
                <Label>Hero subheadline</Label>
                <Input value={template.hero_subheadline || ""} onChange={(e) => update("hero_subheadline", e.target.value)} />
              </div>
            </div>

            {(template.logo_url || template.hero_photo_url) && (
              <div className="border rounded-lg p-4 bg-muted/30">
                <div className="text-xs text-muted-foreground mb-2">Preview</div>
                <div className="flex items-center gap-4">
                  {template.logo_url && <img src={template.logo_url} alt="logo" className="h-10 object-contain bg-white rounded p-1 border" />}
                  {template.hero_photo_url && <img src={template.hero_photo_url} alt="hero" className="h-16 w-32 object-cover rounded border" />}
                </div>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* ABOUT */}
        <TabsContent value="about" className="mt-6">
          <Card className="p-6 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>About headline</Label>
                <Input value={template.about_headline || ""} onChange={(e) => update("about_headline", e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Years in business</Label>
                  <Input type="number" value={template.years_in_business || ""} onChange={(e) => update("years_in_business", e.target.value ? Number(e.target.value) : null)} />
                </div>
                <div>
                  <Label>License #</Label>
                  <Input value={template.license_number || ""} onChange={(e) => update("license_number", e.target.value)} />
                </div>
              </div>
            </div>
            <div>
              <Label>About body / why choose us</Label>
              <Textarea rows={5} value={template.about_body || ""} onChange={(e) => update("about_body", e.target.value)} />
            </div>
            <div>
              <Label>Team photo URL</Label>
              <Input value={template.team_photo_url || ""} placeholder="https://…/team.jpg" onChange={(e) => update("team_photo_url", e.target.value)} />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Certifications &amp; badges</Label>
                <Button variant="ghost" size="sm" onClick={() => update("certifications", [...(template.certifications || []), { name: "", logo_url: "" }])} className="gap-1">
                  <Plus className="w-3 h-3" /> Add
                </Button>
              </div>
              <div className="space-y-2">
                {(template.certifications || []).map((c: CertificationItem, i: number) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input placeholder="GAF Master Elite" value={c.name} onChange={(e) => {
                      const arr = [...(template.certifications || [])]; arr[i] = { ...arr[i], name: e.target.value }; update("certifications", arr);
                    }} />
                    <Input placeholder="Logo URL" value={c.logo_url || ""} onChange={(e) => {
                      const arr = [...(template.certifications || [])]; arr[i] = { ...arr[i], logo_url: e.target.value }; update("certifications", arr);
                    }} />
                    <Button variant="ghost" size="icon" onClick={() => {
                      const arr = [...(template.certifications || [])]; arr.splice(i, 1); update("certifications", arr);
                    }}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* BROCHURES */}
        <TabsContent value="brochures" className="mt-6">
          <Card className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold">Manufacturer brochures library</h3>
                <p className="text-xs text-muted-foreground">Reps can attach any of these to a proposal. Pre-loaded with the major brands — add or replace as needed.</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => update("brochures", [...(template.brochures || []), { id: `new-${Date.now()}`, manufacturer: "", product_line: "", tier: "better" as const, brochure_url: "" }])} className="gap-1">
                <Plus className="w-4 h-4" /> Add brochure
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(template.brochures || []).map((b: BrochureItem, i: number) => (
                <Card key={b.id || i} className="p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <Badge variant={b.tier === "best" ? "default" : b.tier === "better" ? "secondary" : "outline"}>{b.tier}</Badge>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => {
                      const arr = [...(template.brochures || [])]; arr.splice(i, 1); update("brochures", arr);
                    }}><Trash2 className="w-3 h-3 text-destructive" /></Button>
                  </div>
                  <Input placeholder="Manufacturer (GAF)" value={b.manufacturer} onChange={(e) => {
                    const arr = [...(template.brochures || [])]; arr[i] = { ...arr[i], manufacturer: e.target.value }; update("brochures", arr);
                  }} />
                  <Input placeholder="Product line (Timberline HDZ)" value={b.product_line} onChange={(e) => {
                    const arr = [...(template.brochures || [])]; arr[i] = { ...arr[i], product_line: e.target.value }; update("brochures", arr);
                  }} />
                  <select className="w-full text-sm border rounded-md h-9 px-2 bg-background" value={b.tier} onChange={(e) => {
                    const arr = [...(template.brochures || [])]; arr[i] = { ...arr[i], tier: e.target.value as any }; update("brochures", arr);
                  }}>
                    <option value="good">Good</option>
                    <option value="better">Better</option>
                    <option value="best">Best</option>
                  </select>
                  <div className="flex gap-1">
                    <Input placeholder="Brochure URL (PDF)" value={b.brochure_url} onChange={(e) => {
                      const arr = [...(template.brochures || [])]; arr[i] = { ...arr[i], brochure_url: e.target.value }; update("brochures", arr);
                    }} />
                    {b.brochure_url && (
                      <Button variant="ghost" size="icon" onClick={() => window.open(b.brochure_url, "_blank")}><ExternalLink className="w-4 h-4" /></Button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* TIERS */}
        <TabsContent value="tiers" className="mt-6">
          <Card className="p-6 space-y-4">
            <p className="text-sm text-muted-foreground">
              Default what's-included bullets for each tier. Reps will set the price and colors per job.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {(["good", "better", "best"] as TierKey[]).map((key) => {
                const tier = (template.default_tiers || []).find((t: any) => t.key === key) || DEFAULT_TIERS.find((t) => t.key === key)!;
                return (
                  <Card key={key} className="p-4 space-y-3">
                    <Badge variant={key === "best" ? "default" : key === "better" ? "secondary" : "outline"}>{key.toUpperCase()}</Badge>
                    <div>
                      <Label>Display name</Label>
                      <Input value={tier.name} onChange={(e) => {
                        const arr = (template.default_tiers || []).filter((t: any) => t.key !== key);
                        update("default_tiers", [...arr, { ...tier, name: e.target.value }]);
                      }} />
                    </div>
                    <div>
                      <Label>What's included (one per line)</Label>
                      <Textarea rows={6} value={(tier.included || []).join("\n")} onChange={(e) => {
                        const arr = (template.default_tiers || []).filter((t: any) => t.key !== key);
                        update("default_tiers", [...arr, { ...tier, included: e.target.value.split("\n").filter(Boolean) }]);
                      }} />
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={!!tier.highlight} onCheckedChange={(v) => {
                        const arr = (template.default_tiers || []).map((t: any) => ({ ...t, highlight: t.key === key ? v : false }));
                        update("default_tiers", arr.length ? arr : DEFAULT_TIERS.map((t) => ({ ...t, highlight: t.key === key ? v : false })));
                      }} />
                      <Label className="text-xs">Mark as "Most Popular"</Label>
                    </div>
                  </Card>
                );
              })}
            </div>
          </Card>
        </TabsContent>

        {/* FINANCING & TERMS */}
        <TabsContent value="financing" className="mt-6">
          <Card className="p-6 space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Show financing on proposal</Label>
                <p className="text-xs text-muted-foreground">Customer can apply for financing in one click.</p>
              </div>
              <Switch checked={!!template.financing_enabled} onCheckedChange={(v) => update("financing_enabled", v)} />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Lenders shown to customer</Label>
                <Button variant="ghost" size="sm" onClick={() => update("financing_lenders", [...(template.financing_lenders || []), { name: "", apply_url: "" }])} className="gap-1">
                  <Plus className="w-3 h-3" /> Add lender
                </Button>
              </div>
              <div className="space-y-2">
                {(template.financing_lenders || []).map((l: FinancingLender, i: number) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <Input className="col-span-3" placeholder="Lender name" value={l.name} onChange={(e) => {
                      const arr = [...(template.financing_lenders || [])]; arr[i] = { ...arr[i], name: e.target.value }; update("financing_lenders", arr);
                    }} />
                    <Input className="col-span-4" placeholder="Logo URL (optional)" value={l.logo_url || ""} onChange={(e) => {
                      const arr = [...(template.financing_lenders || [])]; arr[i] = { ...arr[i], logo_url: e.target.value }; update("financing_lenders", arr);
                    }} />
                    <Input className="col-span-4" placeholder="Apply URL" value={l.apply_url || ""} onChange={(e) => {
                      const arr = [...(template.financing_lenders || [])]; arr[i] = { ...arr[i], apply_url: e.target.value }; update("financing_lenders", arr);
                    }} />
                    <Button variant="ghost" size="icon" onClick={() => {
                      const arr = [...(template.financing_lenders || [])]; arr.splice(i, 1); update("financing_lenders", arr);
                    }}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-3 items-end">
              <div className="flex items-center gap-2 md:col-span-1">
                <Switch checked={!!template.deposit_required} onCheckedChange={(v) => update("deposit_required", v)} />
                <Label>Require deposit</Label>
              </div>
              <div>
                <Label>Deposit %</Label>
                <Input type="number" value={template.deposit_percent || 0} onChange={(e) => update("deposit_percent", Number(e.target.value))} disabled={!template.deposit_required} />
              </div>
            </div>

            <div>
              <Label>Default terms &amp; conditions</Label>
              <Textarea rows={5} value={template.default_terms || ""} onChange={(e) => update("default_terms", e.target.value)} />
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
